import { type QueryClient } from '@tanstack/react-query'
import { UserQuery } from '@/features/account/services/User/constants'
import { type Auth0User, type User } from '@/features/account/services/User/types'
import { getGuestStoreParamsSSR } from '@/features/shared/utils/dataFetching/guestStoreParams'
import { type Address, type ValidAddress } from '@/features/account/services/Addresses/types'
import { type BaseUserQuery } from '@/gql/graphql'
import {
  type ShoppingStore,
  type ShoppingStoreParams,
} from '@/features/shop/services/ShoppingStore/types'
import { getMembershipType } from '@/features/account/components/Membership/getMembershipType'
import { getGuestParamsFromCache } from '@/features/account/services/GuestUser/getGuestParamsFromCache'
import { type RequestCookies } from '@shared/request_type'
import { isValidZip } from '@/features/shared/utils/validator'
import { customClaims } from '@/features/shared/serverUtils/auth/constants'

export const getUser = (queryClient: QueryClient) => queryClient.getQueryData<User>([UserQuery])

export const getUserZip = (
  user: Pick<User, 'customer_addresses' | 'default_shopping_address_id'>,
) => getDefaultShoppingAddressWithUser(user)?.zip_code ?? ''

export const getIsGuestUser = ({
  isSessionValid,
  sessionData,
  guestAddress,
}: {
  isSessionValid: boolean
  sessionData: Auth0User | null | undefined
  guestAddress: ValidAddress | undefined
}) => {
  // We need this check below since a user can be on welcome page from auth0 flow,
  // not have created an account and still have the session valid due to auth cookie.
  // So technically this check is to make sure a user is onBoarded from auth0 perspective or be authenticated.
  const isFullyAuthenticated = sessionData
    ? Boolean(sessionData[customClaims.ONBOARDED])
    : isSessionValid

  return !isFullyAuthenticated && isValidZip(guestAddress?.zip_code ?? '')
}

export const getUserAddressParams = ({
  zip,
  zipForZipOnlyUser,
  isGuestUser,
  addressId,
  guestAddress,
}: {
  zip: string
  zipForZipOnlyUser: string | undefined
  isGuestUser: boolean
  addressId: number | undefined
  guestAddress: ValidAddress | undefined
}): ShoppingStoreParams => {
  if (zip || zipForZipOnlyUser) {
    return { address_zip_code: zip || zipForZipOnlyUser }
  }

  const guestExperience = isGuestUser && !addressId

  return guestExperience
    ? {
        address_line_1: guestAddress?.street1,
        address_city: guestAddress?.city,
        address_state: guestAddress?.state,
        address_zip_code: guestAddress?.zip_code ?? '',
      }
    : { address_id: addressId }
}

export const getDefaultShoppingAddressWithUser = (
  user: Pick<User, 'customer_addresses' | 'default_shopping_address_id'>,
) => user.customer_addresses?.find((address) => address.id === user.default_shopping_address_id)

export const getUserHasCaliforniaAddress = (addresses: Address[]) => {
  return addresses.some((address) => address.state === 'CA')
}

export const getIsColoradoAddress = (address: { state: string }) => {
  return address.state === 'CO'
}

type GetUserStateProps = {
  user?: User
  cookies: RequestCookies
}
export function getUserStateSSR({ user, cookies }: GetUserStateProps): string {
  const state = user?.id
    ? getDefaultShoppingAddressWithUser(user)?.state
    : getGuestStoreParamsSSR(cookies).guestAddress?.state
  return state || ''
}

export const mapGqlUserToUser = (customer: NonNullable<BaseUserQuery['customer']>): User => {
  const doNotShareSellTa = customer.privacyPreferences
    ?.find((pref) => pref.path === 'privacy.rights')
    ?.preferences?.find((pref) => pref.id === 'do-not-share-sell')?.value

  let planType: string | null = null
  if (customer.subscription?.plan) {
    const { interval, intervalCount } = customer.subscription.plan
    // if this is moved to Schema, this can be part of the BaseUser query
    planType = getMembershipType(interval ?? '', intervalCount ?? 0)
  }

  return {
    platformCustomerId: customer.platformCustomerId,
    hasEbtPaymentMethod: customer.hasEbtPaymentMethod ?? false,
    id: Number(customer.id),
    universal_id: customer.universal_id,
    name: customer.name,
    email: customer.email,
    phone: customer.phone,
    exempt: customer.exempt,
    has_purchased: customer.has_purchased,
    has_placed_first_order: customer.has_placed_first_order,
    has_placed_marketplace_order: customer.has_placed_marketplace_order,
    order_delivery_type: customer.order_delivery_type,
    default_shopping_address_id: Number(customer.defaultAddress?.id ?? 0),
    do_not_share_sell_ta: !!doNotShareSellTa,
    plan_type: planType,
    ...(customer.defaultAddress?.metro
      ? {
          default_address_metro_id: Number(
            // not all user responses are the same
            // oxlint-disable-next-line @typescript-eslint/no-unnecessary-condition
            customer.defaultAddress?.metro?.id ?? 0,
          ),
        }
      : { default_address_metro_id: null }),
    marketing_phone: customer.marketingPhone,
    ...(customer.subscription
      ? {
          subscription: {
            id: customer.subscription.id,
            status: customer.subscription.status,
            plan: {
              id: customer.subscription.plan.id,
            },
            type: customer.subscription.type,
          },
        }
      : {}),
    ...(customer.selectedRetailer
      ? {
          store_id: Number(customer.selectedRetailer.id),
          store: {
            id: Number(customer.selectedRetailer.id),
            //TODO: these properties are actually null, so the customer.json type should be updated -- https://app.clickup.com/t/86b1qebu4
            name: customer.selectedRetailer.name ?? '',
            image: customer.selectedRetailer.image ?? '',
            description: customer.selectedRetailer.description ?? '',
            hero_image_url: customer.selectedRetailer.hero_image_url ?? '',
            background_color: customer.selectedRetailer.background_color ?? '',
            circular_image_url: customer.selectedRetailer.circular_image_url ?? '',
            require_store_transaction_number_from_shopper:
              customer.selectedRetailer.require_store_transaction_number_from_shopper,
          } satisfies ShoppingStore,
        }
      : { store_id: null }),
    ...(customer.selectedLocation
      ? {
          store_location_id: Number(customer.selectedLocation.id),
          metro_id: Number(customer.selectedLocation.metro?.id ?? 0),
          metro: {
            name: customer.selectedLocation.metro?.name ?? '',
          },
        }
      : {
          store_location_id: 0,
          metro: { name: customer.defaultAddress?.metro?.name ?? '' },
          metro_id: Number(customer.defaultAddress?.metro?.id ?? 0),
        }),
    customer_addresses: customer.customerAddresses.map((address) => {
      return {
        ...address,
        id: Number(address.id),
        street2: address.street2 ?? '',
        door_dropoff: !!address.door_dropoff,
        delivery_instructions: address.deliveryInstructions ?? '',
        property_type: address.property_type ?? undefined,
        requested_delivery_location: address.requested_delivery_location ?? undefined,
        shopper_arrive: address.shopper_arrive ?? undefined,
        gate_code: address.gate_code ?? undefined,
      }
    }),
    groups: customer.groups?.map((group) => ({
      id: group.id,
      name: group.name,
      ownerId: group.ownerId,
      businessType: group.businessType ?? null,
      taxExempt: group.taxExempt,
      websiteUrl: group.websiteUrl ?? null,
      companySize: group.companySize ?? null,
    })),
  }
}

export const getIsZipOnlySignedInUser = (user: User | undefined) =>
  !!user && !user.default_shopping_address_id

export const getIsZipOnlySession = (user: User | undefined) => {
  const guestAddress = getGuestParamsFromCache().guestAddress
  const onlyZipInGuestAddressCookie = guestAddress?.zip_code && !guestAddress.street1

  const userIsZipOnly = user && !user.default_shopping_address_id

  return Boolean(userIsZipOnly || onlyZipInGuestAddressCookie)
}
